import moment from 'moment'
import React, { useMemo, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { Button, Flex } from '@chakra-ui/react'

import { Card } from '@/components/Elements'

//test
import useUser from '@/features/users/useUser'

import EditableTable from '@/components/Table/EditableTable'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useDelegation } from '@/features/delegations/useDelegation'
import { getTemplate } from '@/features/offers/constants/emailTemplate'
import { camelToWords, stringToNumber } from '@/utils/stringHelper'
import { createColumnHelper } from '@tanstack/react-table'
import { toast } from 'react-toastify'
import { offerBookingGroupPolicyType } from '../../../../constants/offerEditRights/offerBookingEditRights'
import {
  APPROVAL_TABLE_TYPE,
  buildApproverTableColumn,
  getInvitationButtonText,
} from '../../../helper/approverTableColumn.helper'
import { selectOfferPermissions } from '../../../../slices/offerDetail.slice'
import { ApprovalStatus, BookingApproverRole } from '@eapproval/offer-shared'
import { authSelector } from '@/stores/auth/authSlice'

const columnHelper = createColumnHelper()

const BookingTabApprovalSection = () => {
  const { user } = useSelector(authSelector)

  const offerPermissions = useSelector(selectOfferPermissions)

  const [selectedApprover, setSelectedApprover] = useState([])
  const { setValue } = useFormContext()

  const { prepend: prependApprover, remove: removeApprover } = useFieldArray({
    name: 'booking.approverDetails',
  })
  const bookingApproverDetails = useWatch({ name: `booking.approverDetails` })
  const profitCenterId = useWatch({ name: `subSystems.0.profitCenter._id` })
  const createdBy = useWatch({ name: 'createdBy' })
  const creator = useWatch({ name: 'creator' })
  const teamAssistant = useWatch({ name: 'booking.teamAssistant' })
  const status = useWatch({ name: `status` })

  const dispatch = useDispatch()
  const { useFetchCostCenter } = useCostCenter()
  const { useFetchAllUsers } = useUser()

  const { useFetchAllDelegations } = useDelegation()

  const { data: allUsersResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    sortBy: 'fullName',
    orderBy: 1,
  })

  const { data: profitCenterResponse } = useFetchCostCenter(profitCenterId || '')
  const { data: allDelegationResponse } = useFetchAllDelegations({
    page: 1,
    pageSize: 0,
  })

  const history = useWatch({ name: 'history' })

  const { getValues } = useFormContext()

  //Translated Variables

  const availableUsers = useMemo(() => {
    if (!allUsersResponse?.data) return []

    return allUsersResponse?.data.filter((user) => ![...teamAssistant, creator].includes(user._id))
  }, [allUsersResponse?.data, creator, teamAssistant])

  const canEditRemark = offerPermissions[offerBookingGroupPolicyType.BOOKING_APPROVAL_REMARK]?.edit ?? false
  const canEditBookingApproval = offerPermissions[offerBookingGroupPolicyType.BOOKING_APPROVAL]?.edit ?? false

  const bookingApproverTableData = useMemo(() => {
    if (
      !Array.isArray(bookingApproverDetails) ||
      !profitCenterResponse?.approvers ||
      !allUsersResponse?.data ||
      !allDelegationResponse?.data
    ) {
      return []
    }
    return bookingApproverDetails.map((detail, index) => {
      let users = []
      if (detail.type === 'technical' || detail.type === 'commercial') {
        users = profitCenterResponse.approvers
          .filter(
            (approverDetail) =>
              approverDetail?.user?._id &&
              approverDetail?.user?._id !== '' &&
              approverDetail?.type === detail.type &&
              approverDetail?.level === detail.level
          )
          .map((approverDetail) => approverDetail.user)
      } else if (detail.type === ContributorGroupType.TECHNICAL_PROJECT_MANAGER) {
        users = availableUsers.filter((user) =>
          [ContributorGroupType.TECHNICAL_PROJECT_MANAGER, ContributorGroupType.SALES].includes(user.contributorGroup)
        )
      } else {
        users = availableUsers.filter((user) => user.contributorGroup === detail.type || detail.type === 'other')
      }

      const userOptions = users.map((user) => {
        return {
          value: user._id,
          label: user.fullName,
        }
      })

      // The assigned user may not be part of the computed options (e.g. the
      // profit center's approver list changed since assignment), so always
      // keep them selectable/visible instead of silently disappearing.
      const selectedUserId = detail?.user?._id || (typeof detail?.user === 'string' ? detail.user : '')
      if (selectedUserId && !userOptions.some((option) => option.value === selectedUserId)) {
        const selectedUserFromList = allUsersResponse.data.find((candidate) => candidate._id === selectedUserId)
        userOptions.push({
          value: selectedUserId,
          label: detail?.user?.fullName || selectedUserFromList?.fullName || selectedUserId,
        })
      }

      let delegationRemark = ''
      let isEffective = true

      if ([ApprovalStatus.APPROVED, ApprovalStatus.REJECTED].includes(detail?.status)) {
        if (detail?.lastActionUser?._id && detail?.lastActionUser?._id !== detail?.user?._id) {
          delegationRemark = `${detail?.status} by delegatee, ${detail?.lastActionUser?.fullName}`
        }
      } else {
        const activeDelegation = allDelegationResponse.data
          .sort((a, b) => new Date(a.validStartDate) - new Date(b.validStartDate))
          .find((row) => {
            const validStartDate = moment(row.validStartDate)
            const validEndDate = moment(row.validEndDate)

            if (!validStartDate || !validStartDate.isValid() || !validEndDate || !validEndDate.isValid()) {
              return false
            }
            //Not started workflow and the delegator is this approver
            return (
              row.delegatedCostCenter.includes(profitCenterId) &&
              //find delegation that is not yet ended
              validEndDate.isSameOrAfter(moment(), 'days') &&
              row.delegator === detail?.user?.fullName
            )
          })

        if (activeDelegation && activeDelegation?.delegatee) {
          delegationRemark = `Delegated to ${activeDelegation?.delegatee} from ${moment(activeDelegation?.validStartDate).format('DD-MMM-YYYY')} to ${moment(activeDelegation?.validEndDate).format('DD-MMM-YYYY')}`
          isEffective =
            activeDelegation &&
            moment().isBetween(activeDelegation.validStartDate, activeDelegation.validEndDate, 'day', '[]')
        }
      }

      if (detail.level === 4 && detail.type !== BookingApproverRole.OTHER) {
        delegationRemark = ''
      }

      const typeDisplay = ['technical', 'commercial'].includes(detail?.type)
        ? `${detail?.level}${detail?.type.charAt(0).toUpperCase()}`
        : camelToWords(detail?.type)

      const approverUserId = detail?.user?._id || detail?.user
      const invitedUsers = Array.isArray(detail.invitedUsers) ? detail.invitedUsers : []
      const invitationButtonText = getInvitationButtonText(invitedUsers)

      const canEditType = detail.isNew ?? false
      return {
        //Type
        type: typeDisplay,
        canEditType: canEditType,
        typeOptions: [
          {
            value: 'legal',
            label: 'Legal',
          },
          {
            value: 'qs',
            label: 'QS',
          },
          {
            value: 'other',
            label: 'Other',
          },
        ],
        level: detail.level,
        user: detail?.user?._id,
        dueDate: detail.dueDate,
        status: detail.status,
        attachments: detail?.attachments,
        userOptions: userOptions,
        remark: detail?.remark,

        delegationRemark: delegationRemark,
        isEffective: isEffective,
        //Control

        disableEditType: !detail.isNew,
        disableEditUser: false,
        disableEditDueDate: [ApprovalStatus.APPROVED, ApprovalStatus.REJECTED].includes(detail.status),
        disableEditRemark: !canEditRemark,
        disableRemoveApprover:
          [ApprovalStatus.APPROVED, ApprovalStatus.REJECTED].includes(detail.status) || !canEditType,

        //Invitation

        invitedUsers: invitedUsers,

        invitationButtonText: invitationButtonText,
        canManageInvitations: String(approverUserId || '') === String(user?._id || ''),
      }
    })
  }, [
    canEditRemark,
    allDelegationResponse?.data,
    allUsersResponse?.data,
    availableUsers,
    bookingApproverDetails,
    profitCenterResponse?.approvers,
    status,
  ])

  const columnDef = useMemo(
    () =>
      buildApproverTableColumn({
        columnHelper,
        dispatch,
        updateInvitationParameter: {
          offerId: getValues('_id'),
        },
        tableType: APPROVAL_TABLE_TYPE.BOOKING_APPROVAL,
        removeApprover,
      }),
    [removeApprover, dispatch, getValues]
  )

  const handleAddApprover = () => {
    prependApprover({
      type: '',
      level: 4,
      user: {
        _id: '',
        email: '',
      },
      dueDate: undefined,
      status: 'Upcoming',
      isNew: true,
      attachments: [],
      invitedUsers: [],
    })
  }

  const handleSendEmail = () => {
    const selectedApproverIndex = selectedApprover.map((stringIndex) => stringToNumber(stringIndex))
    const allBookingApprovers = getValues('booking.approverDetails')
    const offerId = getValues('_id') || ''
    const projectName = getValues('projectName') || ''
    const userDetails = []

    if (selectedApproverIndex.length === 0) {
      toast.error('Please select at least one approver to send email.')
      return
    }

    for (const approverIndex of selectedApproverIndex) {
      if (approverIndex > allBookingApprovers.length) {
        //Should not happen
        break
      }
      const approverDetail = allBookingApprovers[approverIndex]
      if (!approverDetail?.user || !approverDetail?.user?.email || approverDetail?.user?.email === '') {
        continue
      }
      const role = [BookingApproverRole.TECHNICAL, BookingApproverRole.COMMERCIAL].includes(approverDetail.type)
        ? `level ${approverDetail.level} ${approverDetail.type} approver`
        : camelToWords(approverDetail.type)
      const reminder = `You are the ${role} of the offer, please helper to approve the booking of offer: ${projectName}`
      userDetails.push({
        email: approverDetail.user.email,
        name: approverDetail.user.fullName,
        dueDate: moment(approverDetail.dueDate).format('DD-MMM-YYYY'),
        url: '',
        reminder,
      })

      if (userDetails.length > 0) {
        const quotationNumber = getValues('quotationNumber') || ''
        const subject = `Review For EApproval(${quotationNumber}): ${projectName} `
        const emailTo = userDetails.map((userDetail) => userDetail.email).join(';')
        const htmlBody = getTemplate(userDetails, offerId)

        window.open(`mailto:${emailTo}?subject=${subject}&body=${encodeURIComponent(htmlBody)}`)
      } else {
        toast.error('Missing Selected User to create email template. ')
      }
    }
  }
  const handleUpdateBookingTableData = (rowIndex, columnId, value) => {
    const chosenUser = Array.isArray(allUsersResponse?.data)
      ? allUsersResponse.data.find((user) => user._id === value)
      : undefined

    switch (columnId) {
      case 'user':
        if (chosenUser) {
          setValue(`booking.approverDetails.${rowIndex}.user`, chosenUser, {
            shouldDirty: true,
          })
        } else {
          setValue(`booking.approverDetails.${rowIndex}.user`, { _id: '' }, { shouldDirty: true })
        }
        break
      case 'dueDate':
      case 'status':
      case 'remark':
      case 'type':
        setValue(`booking.approverDetails.${rowIndex}.${columnId}`, value, {
          shouldDirty: true,
        })
        break

      default:
        throw new Error(`[CLIENT] Unexpected Error: unhandled column id update ${columnId}! `)
    }
  }

  return (
    <Card
      title="Booking Approvals"
      rightElement={
        canEditBookingApproval && (
          <Flex gap={3}>
            <Button colorScheme="blue" onClick={handleSendEmail}>
              Email
            </Button>
            <Button colorScheme="green" onClick={handleAddApprover}>
              Add Approver
            </Button>
          </Flex>
        )
      }
    >
      <EditableTable
        columnDef={columnDef}
        data={bookingApproverTableData}
        updateData={handleUpdateBookingTableData}
        disabled={!canEditBookingApproval}
        canSelect={true}
        rowSelection={selectedApprover}
        onRowSelect={setSelectedApprover}
        tableWidth="100%"
      />
    </Card>
  )
}

export default BookingTabApprovalSection
